import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Send, CheckCircle, AlertCircle, Loader2, MessageSquare } from 'lucide-react';
import Header from '../../components/ui/Header';
import { supabase } from '../../lib/supabase';

const CONTACT_CATEGORIES = [
  { value: 'donation', label: 'Donation Inquiry' },
  { value: 'site_issue', label: 'Site Issue / Bug Report' },
  { value: 'prayer', label: 'Prayer Request' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'account', label: 'Account Help' },
  { value: 'content', label: 'Content / Scripture Question' },
  { value: 'partnership', label: 'Partnership / Ministry Inquiry' },
  { value: 'other', label: 'Other' },
];

// Simple math-based human verification
const generateCaptcha = () => {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return { a, b, answer: a + b };
};

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    message: '',
  });
  const [captcha, setCaptcha] = useState(() => generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const refreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setCaptchaInput('');
    setCaptchaError(false);
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setCaptchaError(false);

    // Validate captcha
    if (parseInt(captchaInput, 10) !== captcha?.answer) {
      setCaptchaError(true);
      refreshCaptcha();
      return;
    }

    if (!validateEmail(formData?.email)) {
      setErrorMessage('Please enter a valid email address.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const categoryLabel = CONTACT_CATEGORIES?.find(c => c?.value === formData?.category)?.label || formData?.category;

      const { error } = await supabase?.functions?.invoke('send-contact-email', {
        body: {
          name: formData?.name,
          email: formData?.email,
          category: categoryLabel,
          message: formData?.message,
        },
      });

      if (error) throw error;

      setStatus('success');
      setFormData({ name: '', email: '', category: '', message: '' });
      refreshCaptcha();
    } catch (err) {
      console.error('Contact form error:', err);
      setErrorMessage('Failed to send your message. Please try again later.');
      setStatus('error');
      refreshCaptcha();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Contact Us | Fortified Mind — Scripture Memorization & Bible Study</title>
        <meta name="description" content="Get in touch with Fortified Mind. Submit prayer requests, report issues, ask about donations, or inquire about ministry partnerships. We'd love to hear from you." />
        <meta name="keywords" content="contact Fortified Mind, prayer request, Christian ministry contact, Bible study support, SDA ministry contact, scripture memorization help" />
        <link rel="canonical" href="https://fortifiedmind.org/contact-us" />
        <meta property="og:title" content="Contact Us | Fortified Mind" />
        <meta property="og:description" content="Get in touch with Fortified Mind for prayer requests, support, donations, or ministry partnerships." />
        <meta property="og:url" content="https://fortifiedmind.org/contact-us" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-10 pb-20">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
              <MessageSquare className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Contact Us</h1>
          </div>
          <p className="text-muted-foreground">
            Have a question, prayer request, or need help? We'd love to hear from you. Fill out the form below and we'll get back to you as soon as possible.
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl shadow-warm p-6 md:p-8">
          {/* Success state */}
          {status === 'success' && (
            <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/20 rounded-xl mb-6">
              <CheckCircle className="w-5 h-5 text-success mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-foreground">Message sent!</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Thank you for reaching out. We'll get back to you as soon as possible.
                </p>
              </div>
            </div>
          )}

          {/* Error state */}
          {status === 'error' && (
            <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-xl mb-6">
              <AlertCircle className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name + Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="name">
                  Your Name <span className="text-destructive">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData?.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="email">
                  Email Address <span className="text-destructive">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="category">
                Category <span className="text-destructive">*</span>
              </label>
              <select
                id="category"
                name="category"
                required
                value={formData?.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition"
              >
                <option value="" disabled>Select a category…</option>
                {CONTACT_CATEGORIES?.map(cat => (
                  <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5" htmlFor="message">
                Message <span className="text-destructive">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                value={formData?.message}
                onChange={handleChange}
                placeholder="Please describe your question or concern in detail…"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition resize-none"
              />
            </div>

            {/* Human Verification */}
            <div className="bg-muted/40 border border-border rounded-lg p-4">
              <p className="text-sm font-medium text-foreground mb-3">Human Verification</p>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-muted-foreground">
                  What is <strong className="text-foreground">{captcha?.a} + {captcha?.b}</strong>?
                </span>
                <input
                  type="number"
                  value={captchaInput}
                  onChange={(e) => { setCaptchaInput(e?.target?.value); setCaptchaError(false); }}
                  placeholder="Answer"
                  className={`w-24 px-3 py-1.5 border rounded-lg text-sm bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition ${captchaError ? 'border-destructive' : 'border-border'}`}
                />
                <button
                  type="button"
                  onClick={refreshCaptcha}
                  className="text-xs text-primary hover:underline"
                >
                  New question
                </button>
              </div>
              {captchaError && (
                <p className="text-xs text-destructive mt-2">Incorrect answer. Please try again.</p>
              )}
            </div>

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground font-semibold py-2.5 px-6 rounded-lg transition-colors text-sm"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Message
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer note */}
        <p className="text-xs text-muted-foreground text-center mt-6">
          © {new Date()?.getFullYear()} Fortified Mind ·{' '}
          <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
          {' · '}
          <Link to="/terms-of-service" className="hover:underline">Terms of Service</Link>
        </p>
      </main>
    </div>
  );
};

export default ContactUs;
